import { apiPaths } from "@/lib/api/backendUrls"
import { dispatchRefreshTokenRevoked } from "@/features/auth/refreshTokenRevoked"
import { readOAuthErrorCode, SessionExpiredError } from "@/features/auth/sessionExpired"
import { tokenStorage } from "@/features/auth/tokenStorage"
import { ApiError, fetchJson, postFormUrlEncoded, type TokenResponse } from "@/lib/api/http"

export type ChangePasswordPayload = {
  currentPassword: string
  newPassword: string
}

/** Имя Web Lock — одна очередь refresh на все вкладки origin (иначе ротация refresh даёт invalid_grant). */
const REFRESH_ACCESS_TOKEN_LOCK = "leninka:oauth-refresh"

/** Fallback без LockManager: один refresh в пределах этого JS-контекста (одна вкладка). */
let refreshAccessTokenInFlight: Promise<TokenResponse> | null = null

export { updateMyProfileNames } from "@/lib/api/profileApi"

const CLIENT_ID = "LeninkaArt_App"
const SCOPE = "LeninkaArt offline_access"

/** Как в leninkaart_front/src/api/auth.ts — при логине бэкенд ожидает это значение в captchaToken */
const LOGIN_CAPTCHA_TOKEN = "1q2w3e4r5t6y7u8i9o0p"

export const connectPasswordToken = async (username: string, password: string) => {
  return postFormUrlEncoded<TokenResponse>(apiPaths.connectToken, {
    grant_type: "password",
    client_id: CLIENT_ID,
    scope: SCOPE,
    username,
    password,
    captchaToken: LOGIN_CAPTCHA_TOKEN,
  })
}

const failSessionExpired = () => {
  tokenStorage.clear()
  dispatchRefreshTokenRevoked()
  throw new SessionExpiredError()
}

const executeRefreshAccessToken = async (): Promise<TokenResponse> => {
  const refresh = tokenStorage.getRefreshToken()
  if (!refresh) {
    dispatchRefreshTokenRevoked()
    throw new SessionExpiredError()
  }
  try {
    return await postFormUrlEncoded<TokenResponse>(apiPaths.connectToken, {
      grant_type: "refresh_token",
      client_id: CLIENT_ID,
      scope: SCOPE,
      refresh_token: refresh,
    })
  } catch (e) {
    if (e instanceof ApiError && readOAuthErrorCode(e.body) === "invalid_grant") {
      failSessionExpired()
    }
    throw e
  }
}

/**
 * Обновляет пару токенов. Параметр не используется — refresh читается из `tokenStorage` внутри очереди.
 * Web Locks сериализуют refresh между вкладками; иначе — single-flight в одной вкладке.
 */
export const refreshAccessToken = async (_legacyRefreshToken?: string) => {
  const useWebLocks =
    typeof navigator !== "undefined" && typeof navigator.locks?.request === "function"

  if (useWebLocks) {
    return navigator.locks.request(REFRESH_ACCESS_TOKEN_LOCK, () => executeRefreshAccessToken())
  }

  if (refreshAccessTokenInFlight) {
    return refreshAccessTokenInFlight
  }

  const p = executeRefreshAccessToken()
  refreshAccessTokenInFlight = p
  p.finally(() => {
    if (refreshAccessTokenInFlight === p) {
      refreshAccessTokenInFlight = null
    }
  })
  return p
}

export type RegisterAccountPayload = {
  email: string
  password: string
  captchaToken: string
  name: string
  surname: string
  middleName?: string
}

export const registerAccount = async (payload: RegisterAccountPayload) => {
  const body: Record<string, string> = {
    email: payload.email,
    password: payload.password,
    captchaToken: payload.captchaToken,
    name: payload.name,
    surname: payload.surname,
  }
  const middleName = payload.middleName?.trim()
  if (middleName) {
    body.middleName = middleName
  }
  return fetchJson<unknown>(apiPaths.authRegister, {
    method: "POST",
    body,
  })
}

export type ConfirmEmailPayload = {
  userId: string
  token: string
}

export const confirmEmail = async (payload: ConfirmEmailPayload) => {
  return fetchJson<unknown>(apiPaths.authConfirmEmail, {
    method: "POST",
    body: {
      userId: payload.userId,
      token: payload.token,
    },
  })
}

export const sendPasswordResetLink = async (email: string) => {
  return fetchJson<unknown>(apiPaths.authSendPasswordReset, {
    method: "POST",
    body: { email },
  })
}

export type ResetPasswordPayload = {
  userId: string
  resetToken: string
  newPassword: string
}

export const resetPasswordWithToken = async (payload: ResetPasswordPayload) => {
  return fetchJson<unknown>(apiPaths.authResetPassword, {
    method: "POST",
    body: {
      userId: payload.userId,
      resetToken: payload.resetToken,
      newPassword: payload.newPassword,
    },
  })
}

export const changePassword = async (accessToken: string, payload: ChangePasswordPayload) => {
  return fetchJson<unknown>(apiPaths.authChangePassword, {
    method: "POST",
    token: accessToken,
    body: {
      currentPassword: payload.currentPassword,
      newPassword: payload.newPassword,
    },
  })
}
